import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

export const STREAMLADDER_CONTENT: CompareContent = {
  slug: "crayo-vs-streamladder",
  competitorSlug: "streamladder",
  title: "Crayo vs StreamLadder",
  seoTitle: "Crayo vs StreamLadder: ClipGPT Pricing and Features Compared",
  seoDescription:
    "Crayo vs StreamLadder, with dated prices. StreamLadder gates ClipGPT to Creator and imports Twitch and Kick VODs. Crayo clips YouTube links on its entry plan.",
  cardVerdict:
    "Better than Crayo for Twitch and Kick VODs, free editing and scheduling, behind on AI clipping price, YouTube links and formats.",
  bestFor: "Streamers clipping Twitch or Kick VODs",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick for clippers who want AI clipping on the entry plan: ${money(hobby.monthly)} a month buys ${hobby.credits} finished clips from YouTube links or uploaded VODs, with ranked moments, a face-aware camera and a timeline editor included. StreamLadder is the better pick if you clip Twitch or Kick VODs by URL, want a free watermark-free editor, or schedule posts from the same tool. Crayo imports neither platform. The evidence is below.`,
  pickCrayoIf: [
    `You want AI clipping on the cheapest paid plan: Crayo Hobby is ${money(hobby.monthly)} a month with ${hobby.credits} credits, one per finished clip, while StreamLadder's ClipGPT starts on Creator at CA$37 a month.`,
    "Your sources are YouTube videos: Crayo takes the link directly, while StreamLadder's ClipGPT only accepts YouTube as an uploaded file.",
    "You want a face-aware camera on facecam footage, a timeline editor with keyframes and speed ramps, 32 caption styles in 56 spoken languages and a 34-endpoint API on every plan.",
  ],
  pickThemIf: [
    "You clip Twitch or Kick VODs: StreamLadder takes them by URL, and Crayo does not import either platform.",
    "You want a free editor with no watermark on basic edits, 720p export, and a 7-day trial of everything else. Crayo has no free plan and no trial.",
    "You schedule posts to TikTok, Shorts and Reels from the clipping tool, or you edit on an iOS or Android app. Crayo is a browser app with no scheduler.",
  ],
  wins: [
    {
      claim: "Crayo includes AI clipping on the entry plan; StreamLadder gates ClipGPT to its Creator tier.",
      evidence:
        `On StreamLadder's upgrade page checked Oct 9, 2026, Starter at CA$21 a month lists background export, 1080p 60fps and the Clip Editor but no ClipGPT; unlimited ClipGPT appears on Creator at CA$37 a month. Crayo Hobby at ${money(hobby.monthly)} a month includes AutoClip, Find Viral Moments and the AI Video Editor with ${hobby.credits} workflow credits.`,
      href: "https://app.streamladder.com/upgrade",
    },
    {
      claim: "Crayo clips from a YouTube link; StreamLadder's ClipGPT takes YouTube only as an uploaded file.",
      evidence:
        "StreamLadder's ClipGPT page says Twitch and Kick VODs import by URL and YouTube videos must be uploaded as files. Crayo AutoClip accepts YouTube and TikTok links or a file upload up to 4 GB and 3 hours.",
      href: "https://streamladder.com/clip-gpt",
    },
    {
      claim: "Crayo's camera is face-aware; StreamLadder describes preset layouts.",
      evidence:
        "StreamLadder's clip editor page, checked Oct 8, 2026, describes preset gameplay plus facecam layouts and custom layouts, and does not describe face tracking. Crayo's AutoClip streamer layout and the editor's camera track follow the face.",
      href: "https://www.streamladder.com/clip-editor",
    },
    {
      claim: "Every Crayo plan exports at 1080p; StreamLadder's free tier is 720p at 30fps with a 200 MB upload cap.",
      evidence:
        "StreamLadder's upgrade page checked Oct 9, 2026 lists 720p 30fps export and 200 MB uploads on Free; 1080p 60fps starts on Starter at CA$21 a month. Crayo has no free plan, so there is no reduced tier to outgrow.",
      href: "https://app.streamladder.com/upgrade",
    },
  ],
  concessions: [
    {
      claim: "StreamLadder has a real free editor with no watermark on basic edits, plus a 7-day trial.",
      evidence:
        "The free plan includes the editor, converter and downloader with 720p export, and the homepage offers a 7-day free trial with full access to paid features. Crayo has no free plan and no trial.",
      href: "https://app.streamladder.com/upgrade",
    },
    {
      claim: "StreamLadder imports Twitch and Kick VODs by URL; Crayo does not import either.",
      evidence:
        "The ClipGPT page says it supports Twitch and Kick VODs by URL, and the Creator plan repeats it. Crayo AutoClip takes YouTube and TikTok links or a file upload, so a Twitch VOD must be downloaded first.",
      href: "https://streamladder.com/clip-gpt",
    },
    {
      claim: "StreamLadder schedules posts through its Content Publisher and ships iOS and Android apps.",
      evidence:
        "Content Publisher posts and schedules to TikTok, YouTube Shorts and Instagram Reels, with X on Pro, per the publisher page checked Oct 8, 2026. The App Store listing shows the iOS app. Crayo exports the file for you to post yourself.",
      href: "https://streamladder.com/content-publisher",
    },
    {
      claim: "StreamLadder captions in 90+ languages, exports 4K on Pro, and bundles emote, montage and schedule makers.",
      evidence:
        "The clip editor page lists 90+ caption languages against Crayo's 56 spoken languages. Pro adds 4K 60fps export; Crayo tops out at 1080p. The homepage lists an Emote Maker, Montage Maker and Schedule Maker that Crayo does not have.",
      href: "https://www.streamladder.com/clip-editor",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute stream VOD a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes, with each VOD uploaded as a file or pasted as a YouTube link`,
      note: "Prices from the live plan table. Twitch and Kick VODs must be downloaded first.",
    },
    them: {
      plan: "Creator",
      cost: "CA$37/mo; yearly figure not displayed",
      usage: "ClipGPT is unlimited on Creator and suggests about 10 clips per VOD, so four VODs by Twitch or Kick URL cover 32 clips with nothing metered",
      note: "Starter at CA$21 has no ClipGPT, so clips there are cut by hand.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 2-hour gameplay stream VOD with facecam owned by Crayo",
    plans: "StreamLadder Starter and Crayo Clipper, same file, same day, defaults first then one tuned run each",
  },
  billingNote:
    "StreamLadder's upgrade page rendered in Canadian dollars from our location on Oct 9, 2026 and promised 2 months free on yearly billing without showing the yearly figures; the only USD list is Apple's App Store (Starter $14.99/mo or $149.99/yr, Creator $26.99/mo or $269.99/yr, Pro $38.99/mo or $389.99/yr), which usually carries Apple's margin. Refund and cancellation terms are not on the upgrade page.",
  faqs: [
    {
      question: "Is Crayo cheaper than StreamLadder?",
      answer:
        `For AI clipping on quarterly or yearly billing, yes; on monthly billing the two are a few dollars apart. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} credits, and one credit is one finished clip. StreamLadder's AI clipping, ClipGPT, starts on Creator at CA$37 a month as rendered on its upgrade page on Oct 9, 2026; Apple's App Store lists Creator at $26.99 a month in USD, a little under Crayo's monthly price and above its quarterly and yearly ones. If you only cut clips by hand, StreamLadder's free editor costs nothing, and Starter at CA$21 adds 1080p export, so for manual clipping StreamLadder is cheaper.`,
    },
    {
      question: "Does Crayo import Twitch or Kick VODs like StreamLadder?",
      answer:
        "No. Crayo AutoClip takes YouTube and TikTok links or a file upload up to 4 GB and 3 hours. A Twitch or Kick VOD has to be downloaded first and uploaded as a file. StreamLadder's ClipGPT imports Twitch and Kick VODs by URL, which is the main reason a Twitch or Kick streamer would pick it. The reverse holds for YouTube: Crayo takes the link, and StreamLadder's ClipGPT page says YouTube videos must be uploaded as files.",
    },
    {
      question: "Does Crayo have a free plan or trial like StreamLadder?",
      answer:
        `No. Crayo has no free plan and no trial; the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload the file. The entry plan is Hobby at ${money(hobby.monthly)} a month. StreamLadder's free plan includes its editor, converter and downloader with no watermark on basic edits, capped at 720p 30fps export and 200 MB uploads, and its homepage offers a 7-day trial with full access to paid features.`,
    },
    {
      question: "What is StreamLadder ClipGPT, and what is Crayo's equivalent?",
      answer:
        "ClipGPT is StreamLadder's AI clipper. It scans a finished Twitch or Kick VOD for intense highlights and chat reactions, suggests about 10 clips with a virality score, and is unlimited on the Creator and Pro plans. It works on VODs, not live streams, and YouTube sources must be uploaded as files. Crayo's equivalent is AutoClip: 1 to 20 clips per run, 15 to 120 seconds each, a 500-character brief, four layouts including a streamer layout for gameplay plus facecam, and a virality ranking. Find Viral Moments lists up to 50 ranked moments to review before cutting.",
    },
    {
      question: "How much does StreamLadder cost?",
      answer:
        "StreamLadder's pricing page is an app page that only renders in a browser. Opened from Canada on Oct 9, 2026, it showed Free at CA$0, Starter at CA$21 a month, Creator at CA$37 a month and Pro at CA$53 a month, with 2 months free on yearly billing but no yearly figures displayed. The only official USD list is Apple's App Store: Starter $14.99 a month or $149.99 a year, Creator $26.99 a month or $269.99 a year, Pro $38.99 a month or $389.99 a year, and App Store prices usually carry Apple's margin. ClipGPT needs Creator or Pro; 4K export needs Pro.",
    },
    {
      question: "Which handles a facecam stream better, Crayo or StreamLadder?",
      answer:
        "The two work differently, and we have not run the hands-on test yet. StreamLadder's clip editor page describes preset gameplay plus facecam layouts and custom layouts, and does not describe face tracking. Crayo's AutoClip streamer layout and the editor's camera track are face-aware, with zooms, keyframes and speed ramps on every plan. The scheduled test uses a 2-hour gameplay VOD with facecam on both tools, and the results will be published here whichever way they fall.",
    },
    {
      question: "Does Crayo auto-post or schedule like StreamLadder's Content Publisher?",
      answer:
        "No. Crayo exports the file and you post it yourself. StreamLadder's Content Publisher posts and schedules to TikTok, YouTube Shorts and Instagram Reels, with X on the Pro plan, per its publisher page. Crayo's View Tracker follows Instagram, YouTube and TikTok creators for posting cadence and viral outliers; it is not a scheduler and not post analytics. If scheduling from the clipping tool decides it, StreamLadder wins that row.",
    },
    {
      question: "StreamLadder vs Eklipse vs Crayo: which should a streamer pick?",
      answer:
        "StreamLadder and Eklipse both import Twitch and Kick VODs by URL, so a streamer whose only source is Twitch has two direct options there; StreamLadder also runs a free tier and a 7-day trial, while Eklipse ended its free tier in June 2026. Crayo does not import Twitch or Kick, so it suits clippers who work from YouTube links or uploaded VODs, want the moments ranked before cutting, and want a timeline editor on the entry plan. StreamLadder's AI clipping starts on Creator at CA$37 a month; Crayo's starts on the entry plan. Our separate Eklipse page covers that comparison with dated prices.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. StreamLadder prices checked Oct 9, 2026 in a browser from Canada: Starter CA$21/mo, Creator CA$37/mo with ClipGPT, Pro CA$53/mo with 4K; App Store USD list Starter $14.99/mo, Creator $26.99/mo, Pro $38.99/mo.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-eklipse", "crayo-vs-opus-clip", "crayo-vs-capcut"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs streamladder",
    "streamladder alternative",
    "streamladder vs eklipse",
    "streamladder pricing",
    "streamladder clipgpt",
    "is streamladder free",
  ],
};
